import { Party } from "../component/enums/Party";
import { v4 as uuidv4 } from "uuid";
import { FormikHelpers, FormikValues } from "formik";
import { NavigateFunction } from "react-router-dom";
import { sendIntake } from "../component/intake/IntakeSender";
import { BenefitType } from "../component/enums/BenefitType";
import generatePDF, { Resolution } from "react-to-pdf";
import { defaultPfmlEmployeeFormValues } from "../component/pfml/PfmlEmployeeFormValues";
import { defaultDisabilityEmployeeFormValues } from "../component/disability/DisabilityEmployeeFormValues";
import { defaultHospitalIndemnityEmployeeFormValues } from "../component/indemnity/HospitalIndemnityEmployeeFormValues";
import { defaultWellnessEmployeeFormValues } from "../component/wellness/WellnessEmployeeFormValues";
import { defaultPfmlEmployerFormValues } from "../component/pfml/PfmlEmployerFormValues";
import { defaultDisabilityEmployerFormValues } from "../component/disability/DisabilityEmployerFormValues";
import { defaultHospitalIndemnityEmployerFormValues } from "../component/indemnity/HospitalIndemnityEmployerFormValues";
import { defaultLifeEmployerFormValues } from "../component/life/LifeEmployerFormValues";
import { defaultPfmlPhysicianFormValues } from "../component/pfml/PfmlPhysicianFormValues";
import { defaultDisabilityPhysicianFormValues } from "../component/disability/DisabilityPhysicianFormValues";
import { defaultLifeBeneficiaryFormValues } from "../component/life/LifeBeneficiaryFormValues";
import { ApolloClient } from "@apollo/client";
import _ from "lodash";
import { defaultAccidentEmployeeFormValues } from "../component/accident/AccidentEmployeeFormValues";
import { defaultAccidentEmployerFormValues } from "../component/accident/AccidentEmployerFormValues";
import { defaultCriticalIllnessEmployeeFormValues } from "../component/illness/CriticalIllnessEmployeeFormValues";
import { defaultCriticalIllnessEmployerFormValues } from "../component/illness/CriticalIllnessEmployerFormValues";
import {trackFormEvent} from "../component/navigation/FormViewMetric";

export function gatherInitialValues(routeClaimId: string | undefined | null, benefit: BenefitType, party: Party): FormikValues {
    const claimId = routeClaimId ?? uuidv4();
    pushToGoogleAnalytics(claimId, party);
    return {
        ...findInitialValuesFor(party, benefit),
        claimId: claimId,
        party: party,
    }
}

export async function submitHandler(values: FormikValues, helpers: FormikHelpers<any>,
                                    navigate: NavigateFunction, apolloClient: ApolloClient<any>) {
    await generateSummaryPdf(values, helpers.setFieldValue);
    try {
        const claimNumbers = await Promise.all(Object.values(BenefitType)
            .filter(benefitType => values[benefitType])
            .map(async benefitType => {
                const clonedValues = _.cloneDeep(values)
                const initialValues = findInitialValuesFor(clonedValues.party, benefitType)
                clonedValues.benefit = benefitType;
                clonedValues.premiumTaxation = values.premiumTaxation || findFirstSuffixedTruthyValueFor(clonedValues, benefitType, 'PremiumTaxation')
                clonedValues.rate = values.rate || findFirstSuffixedTruthyValueFor(clonedValues, benefitType, 'Rate')
                clonedValues.employeeContributionPercentage = values.employeeContributionPercentage || findFirstSuffixedTruthyValueFor(clonedValues, benefitType, 'EmployeeContributionPercentage')
                clonedValues.premiumsAddedToEmployeesWages = values.premiumsAddedToEmployeesWages || findFirstSuffixedTruthyValueFor(clonedValues, benefitType, 'PremiumsAddedToEmployeesWages')
                const benefitSpecificValues = removeValuesNotPresentInitiallyFrom(initialValues, clonedValues);
                return sendIntake(benefitSpecificValues, apolloClient)
            }))
        await helpers.setFieldValue('claimNumbers', claimNumbers)
        trackFormEvent('formSubmit', values.claimId)
    } catch (e) {
        trackFormEvent('formError', values.claimId)
        navigate("/error")
    }
}

function findFirstSuffixedTruthyValueFor(values: FormikValues, benefitType: BenefitType, suffix: string) {
    return Object.entries(values).find(([key, value]) => key.toLowerCase().includes(benefitType.toLowerCase()) && key.endsWith(suffix) && !!value)?.[1];
}

export function findInitialValuesFor(party: Party, benefit: BenefitType) {
    if (Party.EMPLOYEE === party) {
        if (BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefit) {
            return defaultPfmlEmployeeFormValues;
        } else if (BenefitType.DISABILITY === benefit) {
            return defaultDisabilityEmployeeFormValues;
        } else if (BenefitType.HOSPITAL_INDEMNITY === benefit) {
            return defaultHospitalIndemnityEmployeeFormValues;
        } else if (BenefitType.ACCIDENT === benefit) {
            return defaultAccidentEmployeeFormValues;
        }  else if (BenefitType.CRITICAL_ILLNESS === benefit) {
            return defaultCriticalIllnessEmployeeFormValues;
        } else if (BenefitType.WELLNESS === benefit) {
            return defaultWellnessEmployeeFormValues;
        }
    } else if (Party.EMPLOYER === party) {
        if(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefit) {
            return defaultPfmlEmployerFormValues;
        } else if(BenefitType.DISABILITY === benefit) {
            return defaultDisabilityEmployerFormValues;
        } else if(BenefitType.HOSPITAL_INDEMNITY === benefit) {
            return defaultHospitalIndemnityEmployerFormValues;
        } else if(BenefitType.LIFE === benefit) {
            return defaultLifeEmployerFormValues;
        } else if(BenefitType.ACCIDENT === benefit) {
            return defaultAccidentEmployerFormValues;
        } else if(BenefitType.CRITICAL_ILLNESS === benefit) {
            return defaultCriticalIllnessEmployerFormValues;
        }
    } else if (Party.PHYSICIAN === party) {
        if(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefit) {
            return defaultPfmlPhysicianFormValues;
        } else if(BenefitType.DISABILITY === benefit) {
            return defaultDisabilityPhysicianFormValues;
        }
    } else if (Party.BENEFICIARY === party) {
        if(BenefitType.LIFE === benefit) {
            return defaultLifeBeneficiaryFormValues;
        }
    }
    return {};
}

async function generateSummaryPdf(values: FormikValues, setFieldValue: Function) {
    const summaryContent = (await generatePDF(
        () => document.getElementById('root'),
        { method: 'build', resolution: Resolution.LOW })).output('datauristring');
    values.summaryContent = summaryContent
    setFieldValue('summaryContent', summaryContent)
}

function removeValuesNotPresentInitiallyFrom(initialValues: FormikValues, values: FormikValues) {
    return _.pick(values, Object.keys(initialValues))
}

function pushToGoogleAnalytics(claimId: string, party: Party) {
    let customerType = 'MEMBER'
    if (party === Party.EMPLOYER) {
        customerType = 'EMPLOYER'
    } else if (party === Party.PHYSICIAN) {
        customerType = 'PROVIDER'
    }
    // @ts-ignore
    var dataLayer = window.dataLayer = window.dataLayer || [];
    dataLayer.push({
        'event': 'info-added',
        'contract': {
            'contract-number': claimId,
        },
        'user-info': {
            'customer-type': customerType
        }
    });
}
